import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import rocket from '@assets/placementpage-images/rocket.png';
import cloud from '@assets/placementpage-images/clouds.png';
import iconOne from '@assets/placementpage-images/rocketOne.svg';
import iconTwo from '@assets/placementpage-images/rocketTwo.svg';
import iconThree from '@assets/placementpage-images/rocketThree.svg';
import iconFour from '@assets/placementpage-images/rocketFour.svg';
import iconFive from '@assets/placementpage-images/rocketFive.svg';
import iconSix from '@assets/placementpage-images/rocketSix.svg';
import iconSeven from '@assets/placementpage-images/rocketSeven.svg';
import NewSectionTitle from '@components/shared/NewSectionTitle';
import tempIcon from '@assets/placementpage-images/tempIcon.svg';
import arrows from '@assets/placementpage-images/arrows.svg';
import almaxLogo from '@assets/placementpage-images/almax-logo.png';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import mobileIconOne from '@assets/placementpage-images/icon-one.svg';
import mobileIconTwo from '@assets/placementpage-images/icon-two.svg';
import mobileIconThree from '@assets/placementpage-images/icon-three.svg';
import mobileIconFour from '@assets/placementpage-images/icon-four.svg';
import mobileIconFive from '@assets/placementpage-images/icon-five.svg';
import arrowsMobileIcon from '@assets/placementpage-images/arrows-mobil-icon.svg';
import mobileCloudIcon from '@assets/placementpage-images/cloud-mobile.png';
const HiringSteps = ({ placementPage }) => {
  const [angle, setAngle] = useState(360);
  const [rocketBottom, setRocketBottom] = useState(150);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeIcons, setActiveIcons] = useState(Array(6).fill(true));
  const isMobile = useMediaQuery('(max-width: 767.98px)');

  const ref = useRef(null);
  const steps = [
    { label: '1', icon: iconThree, text: '1 - 1 Mock Interview' },
    { label: '2', icon: iconSeven, text: 'Hours Of Coding Projects' },
    { label: '3', icon: iconSix, text: 'Resume + Portfolio Building' },
    { label: '4', icon: iconFour || '', text: 'Mentor - Guided Projects' },
    { label: '5', icon: iconOne, text: 'AB-CAT Test' },
    { label: '6', icon: iconTwo, text: 'Mentor - Guided Projects' },
  ];
  const radius = 250;
  const center = 250;

  // Define the activation sequence - this is the order in which icons should be activated
  // Starting from the bottom and going counter-clockwise (matches the animation)
  const activationSequence = [3, 4, 5, 0, 1, 2];

  // Define at what percentage of the animation each icon should be activated
  // For example, when angle is 60 (60/360 = ~16.7% of animation), activate the first icon
  const activationThresholds = [30, 75, 120, 180, 240, 300]; // Adjusted thresholds for earlier activation  // Function to check which icons should be active based on current angle
  const updateActiveIcons = useCallback((currentAngle) => {
    const newActiveIcons = Array(6).fill(false); // Reset all icons to inactive

    for (let i = 0; i < activationThresholds.length; i++) {
      if (currentAngle >= activationThresholds[i]) {
        newActiveIcons[activationSequence[i]] = true; // Activate the current icon
      } else {
        break; // Stop activating further icons
      }
    }

    setActiveIcons(newActiveIcons);
  }, []);

  const outlineItems = [
    {
      id: 1,
      title: 'AB-CAT Test',
      subtitle: 'Ace the test',
      icon: mobileIconOne,
    },
    {
      id: 2,
      title: 'Mentor - Guided Projects',
      subtitle: '10+ projects',
      icon: mobileIconTwo,
    },
    {
      id: 3,
      title: '1:1 Mock Interview',
      subtitle: '20+ 1:1 Mock Interviews',
      icon: mobileIconThree,
      highlighted: true,
    },
    {
      id: 4,
      title: 'Hours of Coding Projects',
      subtitle: '100+ Hours of Coding Practice',
      icon: mobileIconFour,
    },
    {
      id: 5,
      title: 'Resume + Portfolio Building',
      subtitle: '4 LPA Average CTC',
      icon: mobileIconFive,
    },
  ];
  const getCoordinates = (index, total) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    return { x, y };
  };
  useEffect(() => {
    let observer;
    let animationTimeout;
    let animationFrame;

    function startAnimation() {
      let currentAngle = 0;
      let currentBottom = -200;
      const maxAngle = 360;
      const maxBottom = 112;
      setAngle(0);
      setRocketBottom(-200);
      setAnimationComplete(false);
      setActiveIcons(Array(6).fill(false));

      const angleStep = 1;
      const bottomStep = 1;

      const step = () => {
        let continueAnimation = false;

        if (currentAngle < maxAngle) {
          currentAngle += angleStep;
          setAngle(currentAngle);
          updateActiveIcons(currentAngle);
          continueAnimation = true;
        }

        if (currentBottom < maxBottom) {
          currentBottom += bottomStep;
          setRocketBottom(currentBottom);
          continueAnimation = true;
        }

        if (continueAnimation) {
          animationFrame = requestAnimationFrame(step);
        } else {
          setAnimationComplete(true);
          setActiveIcons(Array(6).fill(true));
        }
      };

      animationFrame = requestAnimationFrame(step);
    }

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // 1. Instantly show everything as filled
          setAngle(360);
          setRocketBottom(112);
          setAnimationComplete(true);
          setActiveIcons(Array(6).fill(true));

          // 2. After a short delay, start the animation
          animationTimeout = setTimeout(() => {
            startAnimation();
          }, 1800); // 1.2 seconds delay
        } else {
          // Reset everything if user leaves section
          setAnimationComplete(false);
          setAngle(0);
          setRocketBottom(-200);
          setActiveIcons(Array(6).fill(false));
          clearTimeout(animationTimeout);
          cancelAnimationFrame(animationFrame);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      observer.disconnect();
      clearTimeout(animationTimeout);
      cancelAnimationFrame(animationFrame);
    };
  }, [updateActiveIcons, ref]);

  return (
    <>
      {!isMobile ? (
        <div
          className="w-full pt-8"
          style={{ background: 'linear-gradient(180deg, #FFF4F6 0.12%, #FFF 38.64%)' }}
        >
          <div className="container mx-auto">
            <div className="mb-8 text-center">
              <NewSectionTitle
                text="How We Get You"
                highlightedText="Hired"
                placementPage={placementPage}
                className="text-4xl font-bold"
              />
            </div>
          </div>

          <div
            ref={ref}
            className="mt-16 flex h-[650px] w-full items-end justify-center overflow-hidden"
          >
            <div className="relative flex h-[500px] w-[500px] items-center justify-center rounded-full transition-all duration-1000">
              {/* FIRST LAYER: Background gradient */}
              <div
                className="absolute top-0 left-0 h-full w-full rounded-full"
                style={{
                  background: 'linear-gradient(180deg, #FFF4F6 0.12%, #FFF 38.64%)',
                }}
              ></div>
              {/* SECOND LAYER: Dashed border */}
              <div className="absolute top-1/2 left-1/2 flex h-[495px] w-[495px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-dashed border-[#C4C4C4]"></div>
              {/* THIRD LAYER: Red conic gradient animation that covers the dashed border */}
              <div
                className="absolute top-0 left-0 h-full w-full rounded-full"
                style={{
                  background: `conic-gradient(from 180deg, red ${angle}deg, transparent 0deg)`,
                }}
              ></div>
              {/* FOURTH LAYER: White center circle */}
              <div className="absolute top-1/2 left-1/2 z-20 flex h-[490px] w-[490px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white"></div>
              {/* FIFTH LAYER: Icons and content */}
              <div className="absolute top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2">
                <div className="relative mx-auto h-[500px] w-[500px] rounded-full">
                  {steps.map((step, i) => {
                    const { x, y } = getCoordinates(i, steps.length);

                    const isActive = animationComplete || activeIcons[i];

                    // Calculate text position based on where the icon is on the circle
                    let textStyles = {};

                    // Top position (icon 0)
                    if (i === 0) {
                      textStyles = {
                        left: '50%',
                        top: '-80px',
                        transform: 'translateX(-50%)',
                        textAlign: 'center',
                      };
                    }
                    // Right position (icon 1)
                    else if (i === 1) {
                      textStyles = {
                        left: '140%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        textAlign: 'left',
                      };
                    }
                    // Bottom-right position (icon 2)
                    else if (i === 2) {
                      textStyles = {
                        right: '-260px',
                        bottom: '-30px',
                        textAlign: 'right',
                      };
                    }
                    // Bottom-left position (icon 3)
                    else if (i === 3) {
                      textStyles = {
                        left: '-180px',
                        bottom: '-30px',
                        textAlign: 'left',
                      };
                    }
                    // Left position (icon 4)
                    else if (i === 4) {
                      textStyles = {
                        right: '140%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        textAlign: 'right',
                      };
                    } else if (i === 5) {
                      textStyles = {
                        right: '140%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        textAlign: 'right',
                      };
                    }

                    return (
                      <div
                        key={i}
                        className="absolute z-30"
                        style={{
                          top: y - 24,
                          left: x - 24,
                        }}
                      >
                        {/* Icon in circle - changes background color when active */}
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-500 ${
                            isActive ? 'border-red bg-red' : 'border-[#F2F2F2] bg-white'
                          }`}
                        >
                          <Image
                            src={step.icon}
                            alt="icons"
                            width={25}
                            height={25}
                            className={isActive ? 'brightness-0 invert' : ''}
                          />
                        </div>

                        {/* Text positioned outside the circle */}
                        <div
                          className="absolute lg:whitespace-nowrap xl:whitespace-nowrap"
                          style={textStyles}
                        >
                          <div
                            className={`-mb-1 font-proxima-nova text-lg font-medium leading-tight transition-all duration-300 ${
                              isActive ? 'text-[#F00037]' : 'text-[#1F1F1F] opacity-40'
                            }`}
                          >
                            {step.text}
                          </div>

                          {/* Subtitle text below main text - only visible when active */}
                          {i === 0 && isActive && (
                            <div
                              className={`mt-4 text-right font-proxima-nova text-sm not-italic leading-tight transition-all duration-300 ${
                                isActive
                                  ? 'font-normal text-[#1F1F1F]'
                                  : 'font-normal text-[#838383] opacity-40'
                              }`}
                            >
                              30+ 1:1 Mock Interviews
                            </div>
                          )}
                          {i === 1 && isActive && (
                            <div
                              className={`mt-4 text-right font-proxima-nova text-sm not-italic leading-tight transition-all duration-300 ${
                                isActive
                                  ? 'font-normal text-[#1F1F1F]'
                                  : 'font-normal text-[#838383] opacity-40'
                              }`}
                            >
                              50+ Hours of Coding Practice
                            </div>
                          )}
                          {i === 2 && isActive && (
                            <div
                              className={`mt-4 text-right font-proxima-nova text-sm not-italic leading-tight transition-all duration-300 ${
                                isActive
                                  ? 'font-normal text-[#1F1F1F]'
                                  : 'font-normal text-[#838383] opacity-40'
                              }`}
                            >
                              8.5X Average CTC
                            </div>
                          )}
                          {i === 3 && isActive && (
                            <div
                              className={`mt-4 text-right font-proxima-nova text-sm not-italic leading-tight transition-all duration-300 ${
                                isActive
                                  ? 'font-normal text-[#1F1F1F]'
                                  : 'font-normal text-[#838383] opacity-40'
                              }`}
                            >
                              100% of 10+ projects
                            </div>
                          )}
                          {i === 4 && isActive && (
                            <div
                              className={`mt-4 text-right font-proxima-nova text-sm not-italic leading-tight transition-all duration-300 ${
                                isActive
                                  ? 'font-normal text-[#1F1F1F]'
                                  : 'font-normal text-[#838383] opacity-40'
                              }`}
                            >
                              Ace the test.
                            </div>
                          )}
                          {i === 5 && isActive && (
                            <div
                              className={`mt-4 text-right font-proxima-nova text-sm not-italic leading-tight transition-all duration-300 ${
                                isActive
                                  ? 'font-normal text-[#1F1F1F]'
                                  : 'font-normal text-[#838383] opacity-40'
                              }`}
                            >
                              Work on 10+ projects.
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Rocket animation */}
              {/* Rocket animation */}
              <div
                style={{ bottom: `${rocketBottom}px` }}
                className="absolute z-20 mt-4 transition-all"
              >
                <div
                  className="absolute"
                  style={{
                    right: '50px', // Adjust this value to control the distance from the rocket
                    transform: 'translateX(-100%)', // Moves the arrow to the left of the rocket
                  }}
                >
                  <Image src={arrows} alt="arrow" />
                </div>
                <Image src={rocket} alt="rocket" width={100} height={200} className="absolute" />
              </div>
              {rocketBottom >= 112 && (
                <div className="absolute top-[60px] left-1/2 z-30 -translate-x-1/2 text-center">
                  <span className="block font-proxima-nova text-[32px] font-bold text-[#F00037]">
                    95%
                  </span>
                  <div className="flex items-center justify-center font-proxima-nova text-[16px] font-medium text-[#838383]">
                    <span>Placement Rate in</span>
                    <div className="flex items-center">
                      <Image src={almaxLogo} alt="AlmaX Logo" width={50} height={30} />
                    </div>
                  </div>
                </div>
              )}
              {/* Clouds with duration text */}
              <div className="absolute -bottom-3 z-50 flex flex-col items-center">
                <div className="relative">
                  <Image src={cloud} alt="cloud" />

                  <div className="absolute -bottom-3 z-50 flex flex-col items-center">
                    <div className="relative">
                      <Image src={cloud} alt="cloud" />

                      {/* Duration label text */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                        <div className="font-proxima-nova text-lg font-medium leading-[140%]">
                          <span className="font-proxima-nova text-[28px] font-semibold text-[#F00037]">
                            03
                          </span>
                          <span className="ml-2 font-proxima-nova text-[#1F1F1F]">
                            Months Duration
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            className="w-full pt-8"
            style={{ background: 'linear-gradient(180deg, #FFF4F6 0.12%, #FFF 38.64%)' }}
          >
            <div className="container mx-auto">
              <div className="mb-8 text-center">
                <NewSectionTitle
                  text="How We Get You"
                  highlightedText="Hired"
                  placementPage={placementPage}
                  className="text-4xl font-bold"
                />
              </div>
            </div>

            <div
              className="bg-white p-5 font-proxima-nova"
              style={{ background: 'linear-gradient(180deg, #FFF4F6 0.12%, #FFF 38.64%)' }}
            >
              {/* Section Title */}

              {/* Course Outline Items */}
              <div className="relative mb-6 flex flex-col space-y-8">
                {outlineItems.map((item, index) => (
                  <div key={item.id} className="relative flex items-start">
                    {/* Vertical Line */}
                    {index !== outlineItems.length - 1 && (
                      <div className="absolute left-6 top-12 h-full w-[2px] border-l-2 border-dashed border-[#FFB9C9]"></div>
                    )}

                    {/* Icon */}
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-[rgba(240,0,55,0.12)] bg-[#FFFF] shadow-[0px_2px_20px_0px_rgba(0,0,0,0.06)]">
                      <Image
                        src={item.icon}
                        alt={item.title}
                        width={22}
                        height={22}
                        className="object-contain"
                      />
                    </div>

                    {/* Text Content */}
                    <div className="ml-4">
                      <span className="block font-proxima-nova text-[16px] font-semibold text-[#1F1F1F]">
                        {item.title}
                      </span>
                      <span className="block font-proxima-nova text-[14px] text-[#525252]">
                        {item.subtitle}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative mt-8 flex flex-col items-center justify-center">
                {/* Rocket and Placement Rate Section */}
                <div className="relative -ml-12 flex items-center justify-center">
                  {/* Rocket */}
                  <Image src={arrowsMobileIcon} alt="Rocket" width={48} height={68} />
                  <Image src={rocket} alt="Rocket" width={120} height={180} />
                  {/* Placement Rate Text */}
                  <div className="absolute top-0 left-[200px] flex flex-col items-start gap-2 font-proxima-nova">
                    <span className="font-gellix-regular text-[24px] font-bold text-[#F00037]">
                      95%
                    </span>
                    <p className="font-proxima-nova text-[12px] font-medium leading-tight text-[#838383]">
                      <span className=" whitespace-nowrap  font-proxima-nova text-[12px] font-medium">
                        Placement Rate in
                      </span>{' '}
                      <span className="tex-[14px font-proxima-nova-bold font-semibold text-[#1F1F1F]">
                        AlmaX
                      </span>
                    </p>
                  </div>
                </div>

                {/* Cloud and Duration Section */}
                <div className="relative top-[30px] left-0 mt-4 w-full">
                  <Image src={cloud} alt="Cloud" className="mx-auto" width={500} height={150} />
                  <div className="absolute top-[60%] left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                    <span className="text-3xl font-bold text-[#F00037]">03</span>
                    <span className="ml-2 text-lg text-gray-700">Months Duration</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default HiringSteps;
